import { useRef, useState } from 'react';
import { Loader2, Upload, ImageIcon } from 'lucide-react';
import {
  useMyParapheUrl,
  useMySignatureUrl,
  useUploadMyParaphe,
  useUploadMySignature,
} from '../../hooks/queries/useSignatureCircuits';

function StampSlot({
  title,
  hint,
  url,
  emptyLabel,
  actionLabel,
  replaceLabel,
  onFile,
  pending,
  error,
}: {
  title: string;
  hint: string;
  url: string | null | undefined;
  emptyLabel: string;
  actionLabel: string;
  replaceLabel: string;
  onFile: (file: File) => void;
  pending: boolean;
  error: string | null;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <h2 className="text-base font-semibold text-cime-text-primary">{title}</h2>
      <p className="text-sm text-cime-text-muted mt-1">{hint}</p>
      <div className="flex items-start gap-6 mt-4">
        <div className="w-48 h-24 border border-dashed border-neutral-300 rounded-md flex items-center justify-center bg-neutral-50 flex-shrink-0">
          {url ? (
            <img src={url} alt={title} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-xs text-cime-text-muted flex flex-col items-center gap-1">
              <ImageIcon className="w-5 h-5" />
              {emptyLabel}
            </div>
          )}
        </div>
        <div className="flex-1">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={pending}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-cime-primary text-white text-sm hover:opacity-90 disabled:opacity-50 transition"
          >
            {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {url ? replaceLabel : actionLabel}
          </button>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          <input
            ref={fileRef}
            type="file"
            accept="image/png"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function ProfileSignatureSection() {
  const [sigError, setSigError] = useState<string | null>(null);
  const [parapheError, setParapheError] = useState<string | null>(null);
  const { data: signatureUrl } = useMySignatureUrl();
  const { data: parapheUrl } = useMyParapheUrl();
  const uploadSig = useUploadMySignature();
  const uploadParaphe = useUploadMyParaphe();

  return (
    <section className="border border-neutral-200 rounded-lg bg-white p-6 space-y-8">
      <StampSlot
        title="Ma signature"
        hint="PNG transparent, max 5 Mo. Pour le visa (pas les cases de paraphe)."
        url={signatureUrl}
        emptyLabel="Aucune signature"
        actionLabel="Enregistrer ma signature"
        replaceLabel="Remplacer ma signature"
        pending={uploadSig.isPending}
        error={sigError}
        onFile={(file) => {
          setSigError(null);
          uploadSig.mutate(file, {
            onError: (err) => setSigError(err instanceof Error ? err.message : 'Erreur upload'),
          });
        }}
      />
      <StampSlot
        title="Mes initiales"
        hint="PNG optionnel pour les cases de paraphe. Sinon, initiales générées (prénom + nom)."
        url={parapheUrl}
        emptyLabel="Initiales générées"
        actionLabel="Enregistrer mes initiales"
        replaceLabel="Remplacer mes initiales"
        pending={uploadParaphe.isPending}
        error={parapheError}
        onFile={(file) => {
          setParapheError(null);
          uploadParaphe.mutate(file, {
            onError: (err) => setParapheError(err instanceof Error ? err.message : 'Erreur upload'),
          });
        }}
      />
    </section>
  );
}
